import { useState, useCallback } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Typography,
  CircularProgress,
  Tooltip,
  Box,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { useNavigate } from 'react-router-dom';

// Inline infinity icon — indicates agentic AI capability
const AgentIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.6 6.62c-1.44 0-2.8.56-3.77 1.53L12 10.66 10.48 12h.01L7.8 14.39c-.64.64-1.49.99-2.4.99-1.87 0-3.39-1.52-3.39-3.39S3.53 8.62 5.4 8.62c.91 0 1.76.35 2.44 1.03l1.13 1 1.51-1.34L9.22 8.2C8.2 7.18 6.84 6.62 5.4 6.62 2.42 6.62 0 9.04 0 12s2.42 5.38 5.4 5.38c1.44 0 2.8-.56 3.77-1.53l2.83-2.5.01.01L13.52 12h-.01l2.69-2.39c.64-.64 1.49-.99 2.4-.99 1.87 0 3.39 1.52 3.39 3.39s-1.52 3.38-3.39 3.38c-.9 0-1.76-.35-2.44-1.03l-1.14-1.01-1.51 1.34 1.27 1.12c1.02 1.01 2.37 1.57 3.82 1.57 2.98 0 5.4-2.41 5.4-5.38s-2.42-5.37-5.4-5.37z" />
  </svg>
);

interface StoredPromptSummary {
  id: string;
  displayName: string;
  labels: string[];
}

interface AiPromptMenuProps {
  repository: string;
  branch?: string;
  prNumber?: number;
  label: 'pr' | 'repo' | 'workflow';
  /** Workflow-specific context fields */
  githubRunId?: string;
  githubSha?: string;
  githubRunNumber?: string;
  githubEvent?: string;
  githubJobRef?: string;
  githubWorkflowRef?: string;
}

const useStyles = makeStyles(theme => ({
  menuHeader: {
    padding: theme.spacing(0.5, 2),
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  loadingItem: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(1),
  },
  promptName: {
    fontSize: '0.85rem',
  },
  aiButton: {
    color: theme.palette.primary.main,
  },
}));

export const AiPromptMenu = ({
  repository, branch, prNumber, label,
  githubRunId, githubSha, githubRunNumber, githubEvent, githubJobRef, githubWorkflowRef,
}: AiPromptMenuProps) => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [prompts, setPrompts] = useState<StoredPromptSummary[] | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchPrompts = useCallback(async () => {
    if (prompts !== null) return; // Already loaded
    setLoading(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('gemini-agent');
      const params = new URLSearchParams();
      params.set('labels', label);
      const response = await fetchApi.fetch(`${baseUrl}/prompts?${params}`);
      if (response.ok) {
        const data = await response.json();
        setPrompts(data.prompts ?? []);
      } else {
        setPrompts([]);
      }
    } catch {
      setPrompts([]);
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, fetchApi, label, prompts]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    fetchPrompts();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const buildUrl = (promptId?: string) => {
    const params = new URLSearchParams();
    if (promptId) params.set('promptId', promptId);
    params.set('repository', repository);
    if (branch) params.set('branch', branch);
    if (prNumber !== undefined) params.set('pr_number', String(prNumber));
    if (githubRunId) params.set('github_run_id', githubRunId);
    if (githubSha) params.set('github_sha', githubSha);
    if (githubRunNumber) params.set('github_run_number', githubRunNumber);
    if (githubEvent) params.set('github_event', githubEvent);
    if (githubJobRef) params.set('github_job_ref', githubJobRef);
    if (githubWorkflowRef) params.set('github_workflow_ref', githubWorkflowRef);
    return `/gemini-agent/new?${params}`;
  };

  const handleNewSession = () => {
    handleClose();
    navigate(buildUrl());
  };

  const handleSelectPrompt = (promptId: string) => {
    handleClose();
    navigate(buildUrl(promptId));
  };

  const tooltipLabels: Record<string, string> = {
    pr: 'AI Agent for this PR',
    repo: 'AI Agent for this repo',
    workflow: 'AI Agent for this workflow',
  };

  return (
    <>
      <Tooltip title={tooltipLabels[label] ?? 'AI Agent'}>
        <IconButton
          size="small"
          className={classes.aiButton}
          onClick={handleClick}
        >
          <AgentIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={e => e.stopPropagation()}
      >
        <MenuItem onClick={handleNewSession}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <AgentIcon />
            <Typography variant="body2">New AI Session</Typography>
          </Box>
        </MenuItem>
        {loading && (
          <Box className={classes.loadingItem}>
            <CircularProgress size={16} />
          </Box>
        )}
        {prompts && prompts.length > 0 && (
          <>
            <Divider />
            <Typography className={classes.menuHeader}>
              {label === 'pr' ? 'PR Prompts' : label === 'workflow' ? 'Workflow Prompts' : 'Repo Prompts'}
            </Typography>
            {prompts.map(p => (
              <MenuItem key={p.id} onClick={() => handleSelectPrompt(p.id)}>
                <Typography className={classes.promptName}>{p.displayName}</Typography>
              </MenuItem>
            ))}
          </>
        )}
      </Menu>
    </>
  );
};
